<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { TableHeaderSection, DefaultTable, SearchNoResults } from '@/shared/ui';
  import PositionsTableRow from './PositionsTableRow.vue';
  import type { Position } from '@/entities/Position';
  import { OrderOptions } from '@/shared/lib';
  import { useItemSelection } from '../lib/useItemSelection';
  import { usePositionDelete } from '../lib/usePositionDelete';

  const props = withDefaults(
    defineProps<{
      orderOptions: OrderOptions[];
      positions: Position[];
      loading?: boolean;
      isFilterActive?: boolean;
      searchTerm?: string;
    }>(),
    {
      orderOptions: () => [],
      positions: () => [],
      loading: false,
      isFilterActive: false,
      searchTerm: '',
    }
  );
  const { selectedCount, hasSelected, isSelected, toggleSelection, selectedItems, deselectAll } =
    useItemSelection<Position>();

  const emit = defineEmits<{
    'update:sort': [sort: string, order: string];
    'update:filter': [filters: Record<string, unknown>];
    'filter-clicked': [];
    delete: [ids: number[]];
    edit: [id: number];
    copy: [id: number];
  }>();

  const autoSelectedIds = ref<number[]>([]);

  const {
    openDeleteConfirmationModal,
    showNoSelectionError,
  } = usePositionDelete(
    () => selectedItems.value.length,
    () => selectedItems.value,
    (deletedIds) => {
      emit('delete', deletedIds);
    },
    (ids) => {
      ids.forEach((id) => {
        if (isSelected(id)) {
          toggleSelection(id);
        }
      });
    },
    () => {
      autoSelectedIds.value.forEach((id) => {
        if (isSelected(id)) {
          toggleSelection(id);
        }
      });
      autoSelectedIds.value = [];
    }
  );

  const handleToggleSelection = (id: number) => {
    toggleSelection(id);
  };

  const handleToggleAll = () => {
    const allCurrentlySelected = props.positions.every((pos: Position) => isSelected(pos.id));
    if (allCurrentlySelected) {
      deselectAll();
    } else {
      props.positions.forEach((pos: Position) => {
        if (!isSelected(pos.id)) {
          toggleSelection(pos.id);
        }
      });
    }
  };

  const allSelected = computed(
    () => props.positions.length > 0 && props.positions.every((pos: Position) => isSelected(pos.id))
  );

  const handleSortOptions = (sortCamp: string, orderFlow: string) => {
    emit('update:sort', sortCamp, orderFlow);
  };

  const handleFilter = (filters: Record<string, unknown>) => {
    emit('update:filter', filters);
  };

  const handleFilterClicked = () => {
    emit('filter-clicked');
  };

  const handleEdit = (id: number) => {
    emit('edit', id);
  };

  const handleCopy = (id: number) => {
    emit('copy', id);
  };

  const handleInfo = (_id: number) => {
    // TODO: Implementar modal/drawer de informações do cargo
  };

  const handleOpenDeleteConfirmation = async () => {
    if (!selectedItems.value || selectedItems.value.length === 0) {
      showNoSelectionError();
      return;
    }
    autoSelectedIds.value = [];
    await openDeleteConfirmationModal();
  };

  const handleSingleDelete = async (id: number) => {
    deselectAll();
    handleToggleSelection(id);
    autoSelectedIds.value = [id];
    await openDeleteConfirmationModal();
  };

  const defaultOrderOptions: OrderOptions[] = [
    { label: 'Cargo mais antigo', sort: 'id', order: 'asc' },
    { label: 'Cargo mais recente', sort: 'id', order: 'desc' },
    { label: 'Por ordem alfabética crescente', sort: 'name', order: 'asc' },
    { label: 'Por ordem alfabética decrescente', sort: 'name', order: 'desc' },
  ];

  const orderOptions = computed(() => props.orderOptions || defaultOrderOptions);

  const shouldShowNoResults = computed(
    () => !props.loading && props.positions.length === 0 && (!!props.searchTerm || props.isFilterActive)
  );
</script>

<template>
  <DefaultTable class="positions-table">
    <TableHeaderSection
      :headers="['Cargos', 'Departamento', 'Ações']"
      :item-count="positions.length"
      item-type="cargos"
      :has-selected="hasSelected" 
      :selected-count="selectedCount"
      :selected-documents="selectedItems"
      :all-selected="allSelected"
      :show-column-headers="positions.length > 0"
      :order-options="orderOptions"
      :is-filter-active="isFilterActive"
      :delete-disabled="!hasSelected"
      custom-message="Lista de Cargos"
      :show-item-count="false"
      :th-class="(header, index) =>
        index === 0
          ? 'pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 max-w-[280px] min-w-0 whitespace-nowrap align-middle'
          : 'pl-6 py-4.5 text-left text-xs font-semibold text-gray-07 w-min whitespace-nowrap align-middle'"
      th-checkbox-class="w-20 align-middle text-center"
      @toggle-all="handleToggleAll"
      @update:sort="handleSortOptions"
      @update:filter="handleFilter"
      @filter-clicked="handleFilterClicked"
      @delete="handleOpenDeleteConfirmation"
    />
    <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
      <PositionsTableRow
        v-for="position in positions"
        :key="position.id"
        :position="position"
        :is-selected="isSelected(position.id)"
        @toggle-selection="handleToggleSelection"
        @edit="handleEdit"
        @info="handleInfo"
        @copy="handleCopy"
        @delete="handleSingleDelete"
      />
      <tr v-if="shouldShowNoResults">
        <td colspan="100">
          <SearchNoResults />
        </td>
      </tr>
    </tbody>
  </DefaultTable>
</template>

<style scoped>
.positions-table :deep(table) {
  table-layout: fixed;
}
</style>
